import {
  format as dateFnsFormat,
  formatDistance as dateFnsFormatDistance,
  startOfDay,
  startOfWeek,
  startOfMonth,
  startOfYear,
  endOfDay,
  endOfWeek,
  endOfMonth,
  endOfYear,
} from 'date-fns';

export const toTimestamp = (date) => {
  if (typeof date === 'number') {
    if (date.toString().length === 13) return date;
    else return date * 1000;
  }
  if (date instanceof Date) {
    return date.getTime();
  }
  throw new TypeError('Invalid date: ', date);
};

export const toTimezone = (date, tzOffset) => {
  if (typeof tzOffset === 'number') {
    // if less then 10, that we consider it to be in hours
    if (Math.abs(tzOffset) < 12) {
      tzOffset *= 60;
    }

    const localTzOffset = new Date().getTimezoneOffset();
    date.setMinutes(date.getMinutes() + localTzOffset + tzOffset);
  }
  return date;
};

export const localToUtc = (date) => {
  const localTzOffset = new Date().getTimezoneOffset();
  date.setMinutes(date.getMinutes() - localTzOffset);
  return date;
};

export const localToUtcTs = (date, inSecs = false) => {
  const dt = localToUtc(date);
  return inSecs ? Math.floor(dt.getTime() / 1000) : dt.getTime();
};

// Note: date is considered to be in UTC
// Then we can substract tzOffset specified in opts
// from this date
export const formatDate = (date, format, opts = {}) => {
  if (typeof opts.tzOffset === 'number' && !isNaN(opts.tzOffset)) {
    let tzOffset = opts.tzOffset;
    // if less then 10, that we consider it to be in hours
    if (Math.abs(tzOffset) < 12) {
      tzOffset *= 60;
    }

    if (!(date instanceof Date)) {
      date = new Date(date);
    }
    const localTzOffset = date.getTimezoneOffset();
    date.setMinutes(date.getMinutes() + localTzOffset + tzOffset);
  }
  return dateFnsFormat(date, format, opts);
};

export const formatDistance = (date, bazeDate, opts = {}) => {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }
  if (!(bazeDate instanceof Date)) {
    bazeDate = new Date(bazeDate);
  }

  toTimezone(date, opts.tzOffset);
  toTimezone(date, opts.tzOffset);

  return dateFnsFormatDistance(date, bazeDate, opts);
};

export const formatDistanceToNow = (date, opts) => {
  return formatDistance(date, new Date(), opts);
};

export const formatDuration = (duration, includeSecs = true) => {
  const totalSecs = Math.abs(Math.floor(duration / 1000));
  const secs = totalSecs % 60;
  const mins = Math.floor(totalSecs / 60) % 60;
  const hrs = Math.floor(totalSecs / 3600);

  let formatted = '';

  if (duration < 0) {
    const totalMins = Math.floor(totalSecs / 60);
    if (totalMins !== 0 || includeSecs) {
      formatted += '-';
    }
  }

  if (hrs > 0) {
    formatted += `${hrs}h`;
  }

  formatted += `${hrs > 0 ? ' ' : ''}${mins}m`;

  if (includeSecs) {
    formatted += ` ${secs}s`;
  }

  return formatted;
};

export const formatDurationMins = (durationInMins, includeHrs = true) => {
  const mins = Math.floor(durationInMins % 60);
  const hrs = Math.floor(durationInMins / 60);

  let formatted = '';
  if (hrs > 0 || includeHrs) {
    formatted += `${hrs}h`;
  }

  formatted += ` ${mins}m`;
  return formatted;
};

export const getSeconds = (date, inSecs = false) => {
  const tsInSec = inSecs ? date : Math.floor(toTimestamp(date) / 1000);
  return tsInSec % 60;
};

export const getMinutes = (date, inSecs = false) => {
  const tsInSec = inSecs ? date : Math.floor(toTimestamp(date) / 1000);
  return Math.floor(tsInSec / 60) % 60;
};

export const getHours = (date, inSecs = false) => {
  const tsInSec = inSecs ? date : Math.floor(toTimestamp(date) / 1000);
  return Math.floor(tsInSec / (60 * 60));
};

export const toSeconds = (date) => {
  const ts = toTimestamp(date);
  return Math.floor(ts / 1000);
};

export const startOf = (date, period = 'day') => {
  switch (period) {
    case 'day': return startOfDay(date);
    case 'week': return startOfWeek(date);
    case 'month': return startOfMonth(date);
    case 'year': return startOfYear(date);

    default:
      throw new Error(`Invalid time period ${period}`);
  }
};

export const endOf = (date, period = 'day') => {
  switch (period) {
    case 'day': return endOfDay(date);
    case 'week': return endOfWeek(date);
    case 'month': return endOfMonth(date);
    case 'year': return endOfYear(date);

    default:
      throw new Error(`Invalid time period ${period}`);
  }
};
